import isNil from 'lodash/isNil'
import { useCallback, useMemo, useState } from 'react'

import { useBalanceConfig } from '@features/balanceLedger'

import { useQueryParams } from '../../../hooks/useQueryParams'
import { SuppliersBalanceFilterType } from '../enums/suppliersBalanceFilterType'
import {
  BalanceDirection,
  PendingBalanceInteraction,
  SuppliersBalanceFiltersState,
  SuppliersBalanceFilterValue,
} from '../types/suppliersBalanceFilters'

type RawSuppliersBalanceFilters = {
  [SuppliersBalanceFilterType.Account]?: string | string[]
  [SuppliersBalanceFilterType.BalanceDirection]?: string
  search?: string
}

type SuppliersBalanceQuery = SuppliersBalanceFiltersState & { search?: string }

const defaultQuery = {
  [SuppliersBalanceFilterType.Account]: undefined as string[] | undefined,
  [SuppliersBalanceFilterType.BalanceDirection]: undefined as BalanceDirection | undefined,
  search: undefined as string | undefined,
}

const sanitize = (raw: RawSuppliersBalanceFilters): SuppliersBalanceFiltersState => {
  const out: SuppliersBalanceFiltersState = {}

  const account = raw[SuppliersBalanceFilterType.Account]
  if (Array.isArray(account)) {
    const ids = account.filter((v): v is string => typeof v === 'string' && v.length > 0)
    if (ids.length > 0) {
      out[SuppliersBalanceFilterType.Account] = ids
    }
  } else if (typeof account === 'string' && account.length > 0) {
    out[SuppliersBalanceFilterType.Account] = [account]
  }

  const balanceDirection = raw[SuppliersBalanceFilterType.BalanceDirection]
  if (balanceDirection === 'positive' || balanceDirection === 'negative') {
    out[SuppliersBalanceFilterType.BalanceDirection] = balanceDirection
  }

  return out
}

export type UseSuppliersBalanceFiltersResult = {
  filters: SuppliersBalanceFiltersState
  search: string
  setSearch: (value: string) => void
  activeTypes: SuppliersBalanceFilterType[]
  activeCount: number
  isBarVisible: boolean
  addedTypes: SuppliersBalanceFilterType[]
  addFilter: (type: SuppliersBalanceFilterType) => void
  removeFilter: (type: SuppliersBalanceFilterType) => void
  resetFilter: (type: SuppliersBalanceFilterType) => void
  // `displayValue` is the human-readable value shown to the user (e.g. account labels), used for the `Filter Applied`
  // analytics payload instead of the raw stored value (contact ids, a selection count). Falls back to `String(value)`
  // when the caller doesn't have a nicer label to offer.
  setFilterValue: <T extends SuppliersBalanceFilterType>(
    type: T,
    value: SuppliersBalanceFilterValue<T>,
    displayValue?: string,
  ) => void
  resetAll: () => void
  toggleBar: () => void
  // Analytics: the most recent real filter/search interaction, cleared once its event has fired.
  pendingInteraction?: PendingBalanceInteraction
  clearPendingInteraction: () => void
}

export const useSuppliersBalanceFilters = (): UseSuppliersBalanceFiltersResult => {
  const { filtersUrlQueryKey, filterTypeOrder } = useBalanceConfig()
  const [rawQuery, setQuery] = useQueryParams<SuppliersBalanceQuery>({
    defaultQuery,
    key: filtersUrlQueryKey,
    withDefaultQueryInUrl: false,
  })

  const filters = useMemo(() => sanitize(rawQuery), [rawQuery])

  const search = typeof rawQuery.search === 'string' ? rawQuery.search : ''

  const [pendingInteraction, setPendingInteraction] = useState<PendingBalanceInteraction>()

  const clearPendingInteraction = useCallback(() => {
    setPendingInteraction(undefined)
  }, [])

  const setSearch = useCallback(
    (value: string) => {
      setQuery((prev) => ({ ...prev, search: value || undefined }))

      // Clearing leaves no settled result to report, so drop any interaction still awaiting its fetch to settle
      // rather than letting it fire later against an unrelated `resultsCount`.
      setPendingInteraction(value ? { kind: 'search', searchQueryLength: value.length } : undefined)
    },
    [setQuery],
  )

  const activeTypes = useMemo(
    () =>
      filterTypeOrder.filter(
        (t) => filters[t as unknown as SuppliersBalanceFilterType] !== undefined,
      ) as unknown as SuppliersBalanceFilterType[],
    [filterTypeOrder, filters],
  )

  const activeCount = activeTypes.length

  const [isBarHidden, setIsBarHidden] = useState(false)
  const [addedTypes, setAddedTypes] = useState<SuppliersBalanceFilterType[]>([])

  const isBarVisible = (activeCount > 0 || addedTypes.length > 0) && !isBarHidden

  const setFilterValue = useCallback(
    <T extends SuppliersBalanceFilterType>(type: T, value: SuppliersBalanceFilterValue<T>, displayValue?: string) => {
      const empty = isNil(value) || (Array.isArray(value) && value.length === 0)

      setQuery((prev) => ({
        ...prev,
        [type]: empty ? undefined : value,
      }))
      setAddedTypes((prev) => (prev.includes(type) ? prev : [...prev, type]))

      // Picking "All" (empty) leaves no settled result to report, so drop any interaction still awaiting its fetch
      // to settle rather than letting it fire later against an unrelated `resultsCount`.
      if (empty) {
        setPendingInteraction(undefined)
      } else {
        const filterValue = displayValue ?? String(value)
        setPendingInteraction({ kind: 'filter', filterType: type, filterValue })
      }
    },
    [setQuery],
  )

  const removeFilter = useCallback(
    (type: SuppliersBalanceFilterType) => {
      setQuery((prev) => ({ ...prev, [type]: undefined }))
      setAddedTypes((prev) => prev.filter((added) => added !== type))
      setPendingInteraction(undefined)
    },
    [setQuery],
  )

  const resetFilter = useCallback(
    (type: SuppliersBalanceFilterType) => {
      setQuery((prev) => ({ ...prev, [type]: undefined }))
      setAddedTypes((prev) => (prev.includes(type) ? prev : [...prev, type]))
      setPendingInteraction(undefined)
    },
    [setQuery],
  )

  const resetAll = useCallback(() => {
    setQuery(() => defaultQuery)
    setAddedTypes([])
    setIsBarHidden(false)
    setPendingInteraction(undefined)
  }, [setQuery])

  const addFilter = useCallback((type: SuppliersBalanceFilterType) => {
    setAddedTypes((prev) => (prev.includes(type) ? prev : [...prev, type]))
    setIsBarHidden(false)
  }, [])

  const toggleBar = useCallback(() => {
    setIsBarHidden((hidden) => !hidden)
  }, [])

  return {
    filters,
    search,
    setSearch,
    activeTypes,
    activeCount,
    isBarVisible,
    addedTypes,
    addFilter,
    removeFilter,
    resetFilter,
    setFilterValue,
    resetAll,
    toggleBar,
    pendingInteraction,
    clearPendingInteraction,
  }
}
